import type { DotNetObjectReference } from '../types';

const MIN_COL_WIDTH = 50;

interface ColgroupResizerInstance {
    tableElement: HTMLElement;
    dotNetRef: DotNetObjectReference;
    wrapper: HTMLElement;
    actualTable: HTMLTableElement;
    cols: HTMLCollectionOf<HTMLTableColElement>;
    previewLine: HTMLElement;
    resizeMode: 'fit' | 'grow';
    resizing: {
        colIndex: number;
        startX: number;
        startWidth: number;
        startTableWidth: number;
        neighborIndex: number;
        neighborStartWidth: number;
    } | null;
    preventNextClick: boolean;
    resizeObserver: ResizeObserver | null;
    colgroupObserver: MutationObserver | null;
    handleMouseDown: (e: MouseEvent) => void;
    handleMouseMove: (e: MouseEvent) => void;
    handleMouseUp: (e: MouseEvent) => void;
    handleDblClick: (e: MouseEvent) => void;
    handleClick: (e: MouseEvent) => void;
}

/**
* Module for table column resizing via <colgroup>.
* Supports live resizing by dragging and auto-fit via double-click.
*/
const TableColgroupResizerModule = {
    instances: new Map<string, ColgroupResizerInstance>(),

    /**
     * Initializes a colgroup resizer.
     * @param tableId - The ID of the table wrapper element
     * @param dotNetRef - .NET object reference for callbacks
     * @param resizeMode - Resize strategy: 'fit' (neighbor absorbs delta) or 'grow' (table expands)
     */
    initializeResizer(tableId: string, dotNetRef: DotNetObjectReference, resizeMode: 'fit' | 'grow' = 'fit'): void {
        const tableElement = document.getElementById(tableId);
        if (!tableElement) {
            console.warn(`Table element with id '${tableId}' not found`);
            return;
        }

        const wrapper = tableElement.querySelector<HTMLElement>('.table-container');
        if (!wrapper) {
            console.warn(`Table container not found in '${tableId}'`);
            return;
        }

        const actualTable = wrapper.querySelector<HTMLTableElement>('table');
        if (!actualTable) {
            console.warn(`Actual <table> not found in '${tableId}'`);
            return;
        }

        const colgroup = actualTable.querySelector('colgroup');
        if (!colgroup) {
            console.warn(`<colgroup> not found in '${tableId}'`);
            return;
        }

        const cols = colgroup.getElementsByTagName('col');
        if (!cols.length) {
            console.warn(`No <col> elements found in '${tableId}'`);
            return;
        }

        // Create the preview line inside the wrapper
        const previewLine = document.createElement('div');
        previewLine.className = 'table-resize-preview-line';
        wrapper.style.position = 'relative';
        wrapper.appendChild(previewLine);

        const instance: ColgroupResizerInstance = {
            tableElement,
            dotNetRef,
            wrapper,
            actualTable,
            cols,
            previewLine,
            resizeMode,
            resizing: null,
            preventNextClick: false,
            resizeObserver: null,
            colgroupObserver: null,
            handleMouseDown: null!,
            handleMouseMove: null!,
            handleMouseUp: null!,
            handleDblClick: null!,
            handleClick: null!,
        };

        instance.handleMouseDown = (e: MouseEvent) => this.handleMouseDown(e, instance);
        instance.handleMouseMove = (e: MouseEvent) => this.handleMouseMove(e, instance);
        instance.handleMouseUp = (e: MouseEvent) => this.handleMouseUp(e, instance);
        instance.handleDblClick = (e: MouseEvent) => this.handleDblClick(e, instance);
        instance.handleClick = (e: MouseEvent) => this.handleClick(e, instance);

        tableElement.addEventListener('mousedown', instance.handleMouseDown, true);
        document.addEventListener('mousemove', instance.handleMouseMove);
        document.addEventListener('mouseup', instance.handleMouseUp);
        tableElement.addEventListener('dblclick', instance.handleDblClick, true);
        tableElement.addEventListener('click', instance.handleClick, true);

        // Calculate initial flex column widths
        this.calculateFlexWidths(instance);

        // Watch for container size changes and recalculate flex widths
        instance.resizeObserver = new ResizeObserver(() => {
            if (!instance.resizing) {
                this.calculateFlexWidths(instance);
            }
        });
        instance.resizeObserver.observe(wrapper);

        // Watch for colgroup child changes (column show/hide) and recalculate.
        // MutationObserver callbacks run as microtasks before the browser paints,
        // so widths are corrected in the same visual frame as the DOM change.
        instance.colgroupObserver = new MutationObserver(() => {
            if (!instance.resizing) {
                this.calculateFlexWidths(instance);
            }
        });
        instance.colgroupObserver.observe(colgroup, { childList: true });

        this.instances.set(tableId, instance);
    },

    /**
     * Disposes a colgroup resizer and removes all event listeners.
     * @param tableId - The ID of the table wrapper element
     */
    disposeResizer(tableId: string): void {
        const instance = this.instances.get(tableId);
        if (!instance) return;

        instance.tableElement.removeEventListener('mousedown', instance.handleMouseDown, true);
        document.removeEventListener('mousemove', instance.handleMouseMove);
        document.removeEventListener('mouseup', instance.handleMouseUp);
        instance.tableElement.removeEventListener('dblclick', instance.handleDblClick, true);
        instance.tableElement.removeEventListener('click', instance.handleClick, true);

        if (instance.resizeObserver) {
            instance.resizeObserver.disconnect();
            instance.resizeObserver = null;
        }

        if (instance.colgroupObserver) {
            instance.colgroupObserver.disconnect();
            instance.colgroupObserver = null;
        }

        if (instance.previewLine.parentNode) {
            instance.previewLine.remove();
        }

        this.instances.delete(tableId);
    },

    /**
     * Calculates and distributes flex column widths proportionally
     * across the remaining space after subtracting fixed columns.
     * Respects minWidth, maxWidth and corrects rounding errors.
     */
    calculateFlexWidths(instance: ColgroupResizerInstance): void {
        const containerWidth = instance.wrapper.clientWidth;
        if (containerWidth <= 0) return;

        // Refresh cols reference in case Blazor re-rendered the colgroup
        const colgroup = instance.actualTable.querySelector('colgroup');
        if (colgroup) {
            instance.cols = colgroup.getElementsByTagName('col');
        }

        let usedWidth = 0;
        let totalFlex = 0;
        const flexIndices: number[] = [];

        // Step 1: Identify fixed vs flex columns, sum up fixed widths.
        // Check data-flex FIRST — flex cols may already have an inline width
        // from a previous calculateFlexWidths call.
        for (let i = 0; i < instance.cols.length; i++) {
            const col = instance.cols[i];

            if (col.hasAttribute('data-flex')) {
                // Flex column — clear stale inline width so it can be recalculated
                col.style.width = '';
                totalFlex += parseFloat(col.getAttribute('data-flex')!) || 1;
                flexIndices.push(i);
            } else {
                // Fixed column (data-fixed or pinned with explicit width)
                usedWidth += this.getColWidth(instance, i);
            }
        }

        if (flexIndices.length === 0 || totalFlex === 0) {
            // All columns have predefined widths — set the table width to the
            // exact column sum so table-layout: fixed doesn't redistribute space.
            this.recalcTableWidth(instance);
            return;
        }

        // Step 2: Distribute remaining space by flex ratio, clamped to min/max
        const remainingWidth = Math.max(0, containerWidth - usedWidth);
        const widths: number[] = new Array(flexIndices.length);

        for (let j = 0; j < flexIndices.length; j++) {
            const col = instance.cols[flexIndices[j]];
            const flex = parseFloat(col.getAttribute('data-flex')!) || 1;
            const minW = parseFloat(col.getAttribute('data-min-width')!) || MIN_COL_WIDTH;
            const maxWAttr = col.getAttribute('data-max-width');
            const maxW = (maxWAttr && maxWAttr !== '') ? parseFloat(maxWAttr) : Infinity;

            const flexWidth = (flex / totalFlex) * remainingWidth;
            widths[j] = Math.round(Math.min(maxW, Math.max(minW, flexWidth)));
        }

        // Step 3: Correct rounding errors on the last flex column
        const totalFlexUsed = widths.reduce((a, b) => a + b, 0);
        const diff = Math.round(remainingWidth) - totalFlexUsed;
        if (diff !== 0) {
            widths[widths.length - 1] += diff;
        }

        // Step 4: Apply widths to <col> elements
        for (let j = 0; j < flexIndices.length; j++) {
            instance.cols[flexIndices[j]].style.width = widths[j] + 'px';
        }

        // Step 5: Recalculate table width
        this.recalcTableWidth(instance);
    },

    /**
     * Sets the table width to the exact column sum.
     * Prevents table-layout: fixed from redistributing
     * surplus pixels to fixed columns (Selectable/Edit).
     */
    recalcTableWidth(instance: ColgroupResizerInstance): void {
        let totalWidth = 0;
        for (let i = 0; i < instance.cols.length; i++) {
            totalWidth += this.getColWidth(instance, i);
        }
        instance.actualTable.style.width = totalWidth + 'px';
    },

    /**
     * Reads the width of a <col> from its inline style.
     */
    getColWidth(instance: ColgroupResizerInstance, index: number): number {
        const inlineWidth = instance.cols[index].style.width;
        if (inlineWidth) {
            const parsed = parseFloat(inlineWidth);
            if (!isNaN(parsed)) return parsed;
        }
        return parseFloat(getComputedStyle(instance.cols[index]).width) || 0;
    },

    /**
     * Sets the width of a <col>.
     */
    setColWidth(instance: ColgroupResizerInstance, index: number, width: number): void {
        instance.cols[index].style.width = width + 'px';
    },

    /**
     * Synchronizes all <col> widths with the actually rendered cell widths.
     * Necessary because table-layout: fixed can redistribute column widths
     * proportionally when the table width exceeds the sum of <col> widths.
     * Also sets the table width to the column sum so that resize
     * operations match the mouse movement 1:1.
     */
    syncColWidthsFromCells(instance: ColgroupResizerInstance): void {
        const headerRow = instance.actualTable.querySelector('thead tr')
                        || instance.actualTable.querySelector('tr');
        if (!headerRow) return;

        let totalWidth = 0;
        for (let i = 0; i < instance.cols.length; i++) {
            const col = instance.cols[i];

            if (col.hasAttribute('data-fixed')) {
                // Re-stamp the declared width so table-layout: fixed cannot redistribute it
                const declared = this.getColWidth(instance, i);
                col.style.width = declared + 'px';
                totalWidth += declared;
                continue;
            }

            const cell = headerRow.children[i] as HTMLElement;
            if (cell) {
                const width = cell.getBoundingClientRect().width;
                col.style.width = width + 'px';
                totalWidth += width;
            }
        }

        // Set table width to exact column sum so table-layout: fixed has no leftover to redistribute
        instance.actualTable.style.width = totalWidth + 'px';
    },

    /**
     * Shows the preview line at a given X position.
     */
    showPreview(instance: ColgroupResizerInstance, clientX: number): void {
        const rect = instance.wrapper.getBoundingClientRect();
        instance.previewLine.style.display = 'block';
        instance.previewLine.style.left = (clientX - rect.left + instance.wrapper.scrollLeft) + 'px';
        instance.previewLine.style.height = instance.wrapper.scrollHeight + 'px';
    },

    /**
     * Hides the preview line.
     */
    hidePreview(instance: ColgroupResizerInstance): void {
        instance.previewLine.style.display = 'none';
    },

    /**
     * Measures the natural content width of a column for auto-fit.
     */
    autoFitColumn(instance: ColgroupResizerInstance, colIndex: number): number {
        const rows = instance.actualTable.querySelectorAll('tr');
        let maxWidth = 0;

        const measurer = document.createElement('span');
        measurer.style.cssText =
            'position:absolute;visibility:hidden;white-space:nowrap;font:inherit;padding:0 14px;';
        document.body.appendChild(measurer);

        rows.forEach(row => {
            const cell = row.children[colIndex] as HTMLElement;
            if (!cell) return;
            const cs = getComputedStyle(cell);
            measurer.style.font = cs.font;
            measurer.style.padding = cs.padding;

            // Exclude tooltip and context-menu content from measurement — they are
            // overlays and should not influence the column's natural content width.
            let text = '';
            cell.childNodes.forEach(node => {
                if (node.nodeType === Node.TEXT_NODE) {
                    text += node.textContent;
                } else if (node instanceof HTMLElement
                    && !node.classList.contains('table-head-cell-tooltip-dropdown')
                    && !node.classList.contains('context-menu-dropdown')) {
                    text += node.textContent;
                }
            });
            measurer.textContent = text;
            maxWidth = Math.max(maxWidth, measurer.offsetWidth + 2);
        });

        document.body.removeChild(measurer);
        return Math.max(maxWidth, MIN_COL_WIDTH);
    },

    /**
     * Finds the next resizable (non-fixed) column after the given index.
     * Returns -1 if no suitable neighbor exists.
     */
    findNeighborIndex(instance: ColgroupResizerInstance, colIndex: number): number {
        for (let i = colIndex + 1; i < instance.cols.length; i++) {
            if (!instance.cols[i].hasAttribute('data-fixed')) {
                return i;
            }
        }
        return -1;
    },

    /**
     * Pins a column by removing its flex attributes so it stays at its current width.
     */
    pinColumn(instance: ColgroupResizerInstance, colIndex: number): void {
        const col = instance.cols[colIndex];
        col.removeAttribute('data-flex');
        col.removeAttribute('data-min-width');
        col.removeAttribute('data-max-width');
    },

    /**
     * Handler for mousedown on a resize handle.
     */
    handleMouseDown(e: MouseEvent, instance: ColgroupResizerInstance): void {
        const target = e.target as HTMLElement;
        if (!target.classList.contains('table-resize-handle')) return;

        e.preventDefault();

        const colIndexStr = target.dataset.columnIndex;
        if (!colIndexStr) return;

        const colIndex = parseInt(colIndexStr, 10);

        // Refresh cols reference in case Blazor re-rendered the colgroup
        const colgroup = instance.actualTable.querySelector('colgroup');
        if (colgroup) {
            instance.cols = colgroup.getElementsByTagName('col');
        }

        if (isNaN(colIndex) || colIndex >= instance.cols.length) return;

        // Sync all col widths to actual rendered cell widths
        // This prevents jumpy resize when table-layout: fixed redistributes space
        this.syncColWidthsFromCells(instance);

        const neighborIndex = this.findNeighborIndex(instance, colIndex);

        instance.resizing = {
            colIndex,
            startX: e.clientX,
            startWidth: this.getColWidth(instance, colIndex),
            startTableWidth: instance.actualTable.getBoundingClientRect().width,
            neighborIndex,
            neighborStartWidth: neighborIndex >= 0 ? this.getColWidth(instance, neighborIndex) : 0,
        };

        target.classList.add('active');
    },

    /**
     * Handler for mousemove – live resizing and preview line.
     */
    handleMouseMove(e: MouseEvent, instance: ColgroupResizerInstance): void {
        if (!instance.resizing) return;

        // Apply resizing UI on first actual mouse movement
        if (!instance.tableElement.classList.contains('table-resizing')) {
            document.body.classList.add('resizing');
            instance.tableElement.classList.add('table-resizing');
        }

        const { colIndex, startX, startWidth, startTableWidth, neighborIndex, neighborStartWidth } = instance.resizing;

        if (instance.resizeMode === 'fit' && neighborIndex >= 0) {
            // Fit mode: neighbor absorbs the delta, table width stays constant
            let delta = e.clientX - startX;
            // Clamp so the resized column stays >= MIN_COL_WIDTH
            delta = Math.max(delta, MIN_COL_WIDTH - startWidth);
            // Clamp so the neighbor stays >= MIN_COL_WIDTH
            delta = Math.min(delta, neighborStartWidth - MIN_COL_WIDTH);

            this.setColWidth(instance, colIndex, startWidth + delta);
            this.setColWidth(instance, neighborIndex, neighborStartWidth - delta);
        } else {
            // Grow mode: table expands, no other column changes
            const delta = e.clientX - startX;
            const newWidth = Math.max(MIN_COL_WIDTH, startWidth + delta);

            // Set column width live
            this.setColWidth(instance, colIndex, newWidth);

            // Adjust table width to exact column sum.
            // No Math.max — otherwise table-layout: fixed redistributes
            // surplus pixels to fixed columns (Selectable/Edit).
            const newTableWidth = startTableWidth + (newWidth - startWidth);
            instance.actualTable.style.width = newTableWidth + 'px';
        }

        this.showPreview(instance, e.clientX);
    },

    /**
     * Handler for mouseup – finish resize and trigger .NET callback.
     */
    handleMouseUp(_e: MouseEvent, instance: ColgroupResizerInstance): void {
        if (!instance.resizing) return;

        const { colIndex, neighborIndex } = instance.resizing;

        // Remove active class from the handle
        const handle = instance.tableElement.querySelector(
            `.table-resize-handle[data-column-index="${colIndex}"]`
        );
        handle?.classList.remove('active');

        // Notify .NET about the final column width
        const newWidth = this.getColWidth(instance, colIndex);
        instance.dotNetRef.invokeMethodAsync<void>('OnResizeEnd', colIndex, newWidth);

        // Pin the column: remove flex attributes so it stays at its resized width
        this.pinColumn(instance, colIndex);

        // In Fit mode, also pin and report the neighbor column
        if (instance.resizeMode === 'fit' && neighborIndex >= 0) {
            const neighborWidth = this.getColWidth(instance, neighborIndex);
            instance.dotNetRef.invokeMethodAsync<void>('OnResizeEnd', neighborIndex, neighborWidth);

            this.pinColumn(instance, neighborIndex);
        }

        // Recalculate remaining flex columns to fill the space
        this.calculateFlexWidths(instance);

        instance.resizing = null;
        document.body.classList.remove('resizing');
        instance.tableElement.classList.remove('table-resizing');
        this.hidePreview(instance);

        // Block the synthesized click from this mouseup, then auto-clear.
        // Without auto-clear, releasing outside the table leaves the flag
        // stuck and swallows the user's next intentional click.
        instance.preventNextClick = true;
        requestAnimationFrame(() => { instance.preventNextClick = false; });
    },

    /**
     * Handler for dblclick – auto-fit a column.
     */
    handleDblClick(e: MouseEvent, instance: ColgroupResizerInstance): void {
        const target = e.target as HTMLElement;
        if (!target.classList.contains('table-resize-handle')) return;

        e.preventDefault();

        const colIndexStr = target.dataset.columnIndex;
        if (!colIndexStr) return;

        const colIndex = parseInt(colIndexStr, 10);

        // Refresh cols reference in case Blazor re-rendered the colgroup
        const colgroup = instance.actualTable.querySelector('colgroup');
        if (colgroup) {
            instance.cols = colgroup.getElementsByTagName('col');
        }

        if (isNaN(colIndex) || colIndex >= instance.cols.length) return;

        // Sync all col widths to actual rendered cell widths
        this.syncColWidthsFromCells(instance);

        const fitWidth = this.autoFitColumn(instance, colIndex);

        // Only resize the target column; no neighbor adjustment
        this.setColWidth(instance, colIndex, fitWidth);

        // Pin the column so it stays at its resized width
        this.pinColumn(instance, colIndex);

        // Recalculate remaining flex columns to fill available space
        this.calculateFlexWidths(instance);

        // Notify .NET about the final column width
        instance.dotNetRef.invokeMethodAsync<void>('OnResizeEnd', colIndex, fitWidth);
    },

    /**
     * Handler for click – prevents click events after a resize.
     */
    handleClick(e: MouseEvent, instance: ColgroupResizerInstance): void {
        const target = e.target as HTMLElement;
        if (instance.preventNextClick || target.closest('.table-resize-handle')) {
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();
            instance.preventNextClick = false;
        }
    },

    /**
     * Recalculates flex column widths for the given table.
     * Call this after Blazor re-renders the colgroup (e.g. column visibility change).
     * @param tableId - The ID of the table wrapper element
     */
    recalculateWidths(tableId: string): void {
        const instance = this.instances.get(tableId);
        if (!instance) return;

        this.calculateFlexWidths(instance);
    },
};

export default TableColgroupResizerModule;
